<template>
  <div class="container">
    <!-- Page Header Search Bar and New Create Button -->
    <div class="page-item">
      <!-- Search Bar -->
      <div class="search-bar">
        <input
          type="text"
          placeholder="Search user role by name..."
          class="search-input"
          v-model="searchFilter"
        />
      </div>

      <!-- New User Role Create Button -->
      <button
        type="button"
        class="btn button-default create-user-role-btn"
        @click="newUserRole"
      >
        Create User Role
      </button>
    </div>

    <!-- Loader -->
    <div v-if="loading" class="loading-spinner">
      <i class="fa fa-spinner fa-spin"></i> Loading...
    </div>

    <!-- User Role Table -->
    <div v-else>
      <div class="table-container">
        <table>
          <thead>
          <tr>
            <th scope="col">User Role</th>
            <th scope="col">Status</th>
            <th scope="col">Actions</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="user in filteredUsers" :key="user.id">
            <td>{{ user.name }}</td>
            <td>
            <span :class="{
              'status-label status-yes': user.is_active === '1',
              'status-label status-no': user.is_active === '0'
            }">
              {{ user.is_active === '1' ? 'Active' : 'Inactive' }}
            </span>
            </td>
            <td>
              <i class="fa fa-edit" @click="editDetails(user.id)"></i>
              <i class="fa fa-eye" aria-hidden="true" @click="viewDetails(user.id)"></i>
            </td>
          </tr>
          <tr v-if="filteredUsers.length === 0">
            <td colspan="3" class="text-center">No data available</td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- show view user role modal -->
    <viewUserRole
        :show-popup="showPopup"
        :role-id="selectedRoleId"
        @close="closePopup"
    />
  </div>
</template>

<script>
import { fetchUserRoleList } from "@/services/dataService";
import viewUserRole from "@/pages/Modal/viewUserRole.vue";

export default {
  components: { viewUserRole },

  data() {
    return {
      loading: false,
      showPopup: false,
      selectedRoleId: null,
      searchFilter: "",
      userList: [],
      token: localStorage.getItem("token_set") || "",
      profileId: localStorage.getItem("profile_id") || ""
    };
  },

  computed: {
    filteredUsers() {
      return this.userList
          .filter(user =>
              user.name.toLowerCase().includes(this.searchFilter.toLowerCase())
          )
          .sort((a, b) => a.name.localeCompare(b.name));
    }
  },

  watch: {
    showPopup(newVal) {
      if (newVal) {
        // Modal is open → hide page scrollbar
        document.body.style.overflow = 'hidden';
      } else {
        // Modal is closed → restore scrollbar
        document.body.style.overflow = '';
      }
    }
  },

  methods: {
    async fetchUsers() {
      try {
        this.loading = true;
        const response = await fetchUserRoleList(this.profileId, this.token);
        this.userList = response.data?.data || [];
      } catch (error) {
        console.error("Error fetching user list:", error);
        this.userList = [];
      } finally {
        this.loading = false;
      }
    },
    viewDetails(id) {
      this.selectedRoleId = id;
      this.showPopup = true;
    },
    editDetails(id) {
      this.$router.push({ path: `/user-role-edit/${id}` });
    },
    newUserRole() {
      this.$router.push({ path: "/new-user-role" });
    },
    closePopup() {
      this.showPopup = false;
      this.selectedRoleId = null;
    }
  },

  mounted() {
    this.fetchUsers();
  }
};
</script>

<style scoped>
/* Container */
.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
}

/* Loading Spinner */
.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

/* Top search and create user section */
.page-item {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 10px;
  margin-bottom: 15px;
}

/* Combined search bar and filter button */
.search-bar {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
}

/* Search input */
.search-input {
  width: 100%;
  padding: 0 14px;
  border-radius: 6px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
  height: 42px; /* fixed height */
}
.search-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.3);
  outline: none;
}

/* Create User Button */
.create-user-role-btn {
  white-space: nowrap;
  padding: 10px 20px;
  border-radius: 6px;
  background-color: #007bff;
  color: #fff;
  border: none;
  cursor: pointer;
  font-size: 14px;
}
.create-user-role-btn:hover {
  background-color: #0069d9;
}

/* Scrollable Table Container */
.table-container {
  width: 100%;
  overflow-x: auto;
}

/* Table */
table {
  width: 100%;
  min-width: 800px; /* ensure scroll if too many cols */
  border-collapse: collapse;
  margin: 10px 0;
  border: none; /* remove table border */
}

/* Table Header */
th {
  text-align: left;
  border-bottom: 2px solid #ddd; /* subtle bottom border for header */
  background-color: #f4f4f4;
  padding: 10px;
  white-space: nowrap;
}

/* Table Data */
td {
  text-align: left;
  border: none; /* remove individual cell borders */
  padding: 10px;
  white-space: nowrap;
  border-bottom: 1px solid #eee; /* row separator */
}

/* Optional: remove last row separator for cleaner look */
tbody tr:last-child td {
  border-bottom: none;
}

/* Status labels */
.status-label {
  display: inline-block;
  min-width: 90px;
  text-align: center;
  padding: 4px 10px;
  border-radius: 5px;
  font-size: 13px;
  font-weight: 500;
  color: #fff;
}
.status-yes {
  background-color: #28a745;
}
.status-no {
  background-color: #dc3545;
}

/* Action icons */
.fa-eye {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
  margin-left: 5px;
}
.fa-eye:hover {
  color: #0056b3;
  transform: scale(1.2);
}
.fa-eye:active {
  transform: scale(1.1);
}
.fa-edit {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
}
.fa-edit:hover {
  color: #0056b3;
  transform: scale(1.2);
}
.fa-edit:active {
  transform: scale(1.1);
}
</style>